import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import { vehicleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    Truck, Route, Camera, Calendar, 
    TrendingUp, Award, Clock, ChevronRight, 
    Loader2, Flame, Phone, AlertTriangle,
    Upload, Video, Image, X, Check, Eye, Fuel, LogOut, Package, Info,
    MapPin, PhoneCall, Navigation, Copy, ExternalLink, ParkingCircle, Car
} from 'lucide-react';
import { toast } from 'sonner';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';

export default function DriverDashboardPage() {
    const { user } = useAuth();
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [vehicles, setVehicles] = useState([]);
    const [damageReports, setDamageReports] = useState([]);
    const [loadingDamageReports, setLoadingDamageReports] = useState(false);
    const [companyEdition, setCompanyEdition] = useState('carrier'); // Edition state
    
    // Damage Report Modal State
    const [showDamageModal, setShowDamageModal] = useState(false);
    const [damageVehicleId, setDamageVehicleId] = useState('');
    const [damageDescription, setDamageDescription] = useState('');
    const [damageMedia, setDamageMedia] = useState(null);
    const [damageMediaPreview, setDamageMediaPreview] = useState(null);
    const [submittingDamage, setSubmittingDamage] = useState(false);
    const [selectedDamageReport, setSelectedDamageReport] = useState(null);
    const fileInputRef = useRef(null);

    // Fuel Refill Modal State
    const [showFuelModal, setShowFuelModal] = useState(false);
    const [fuelLiters, setFuelLiters] = useState('');
    const [fuelCost, setFuelCost] = useState('');
    const [submittingFuel, setSubmittingFuel] = useState(false);

    // Check Out Modal State
    const [showCheckOutModal, setShowCheckOutModal] = useState(false);
    const [checkOutKm, setCheckOutKm] = useState('');
    const [checkOutPhotos, setCheckOutPhotos] = useState({});
    const [uploadingCheckOutPhoto, setUploadingCheckOutPhoto] = useState(null);
    const [submittingCheckOut, setSubmittingCheckOut] = useState(false);
    const [lastInspection, setLastInspection] = useState(null);
    const checkOutFileRefs = useRef({});

    // Help/Assistance Modal State
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showAssistanceModal, setShowAssistanceModal] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [locationError, setLocationError] = useState(null);

    // Shift Supervisor State
    const [shiftSupervisor, setShiftSupervisor] = useState(null);
    const [loadingSupervisor, setLoadingSupervisor] = useState(false);

    // Active Check-In State (for locking Check-Out)
    const [hasActiveCheckIn, setHasActiveCheckIn] = useState(false);

    // Cargo Photo State (Two photos required: rear and side) - Carrier Edition only
    const [showCargoModal, setShowCargoModal] = useState(false);
    const [cargoPhotos, setCargoPhotos] = useState({ rear: null, side: null });
    const [cargoPreviews, setCargoPreviews] = useState({ rear: null, side: null });
    const [uploadingCargo, setUploadingCargo] = useState(false);
    const [cargoAnalysis, setCargoAnalysis] = useState(null);
    const cargoFileRefs = useRef({ rear: null, side: null });

    // Parking Safety State (Personal Edition only)
    const [showParkingSafetyModal, setShowParkingSafetyModal] = useState(false);
    const [parkingPhotos, setParkingPhotos] = useState({ front: null, rear: null });
    const [parkingPreviews, setParkingPreviews] = useState({ front: null, rear: null });
    const [uploadingParkingSafety, setUploadingParkingSafety] = useState(false);
    const [parkingSafetyRecords, setParkingSafetyRecords] = useState([]);
    const parkingFileRefs = useRef({ front: null, rear: null });

    // Check if Carrier edition (show cargo features)
    const isCarrierEdition = companyEdition === 'carrier';
    const CARGO_FEATURES_ENABLED = false;
    
    // Check if Personal edition (show parking safety)
    const isPersonalEdition = companyEdition === 'personal';

    const cargoPhotoTypes = ['rear', 'side'];
    const cargoPhotoLabels = {
        rear: language === 'el' ? 'Πίσω Πόρτα' : 'Rear Door',
        side: language === 'el' ? 'Πλαϊνή Πόρτα' : 'Side Door'
    };

    const checkOutPhotoTypes = ['front', 'back', 'left', 'right', 'interior_front', 'interior_back'];
    const checkOutPhotoLabels = {
        front: language === 'el' ? 'Μπροστά' : 'Front',
        back: language === 'el' ? 'Πίσω' : 'Back',
        left: language === 'el' ? 'Αριστερά' : 'Left Side',
        right: language === 'el' ? 'Δεξιά' : 'Right Side',
        interior_front: language === 'el' ? 'Εσωτερικό Μπροστά' : 'Interior Front',
        interior_back: language === 'el' ? 'Εσωτερικό Πίσω' : 'Interior Back'
    };

    // Parking Safety photo types and labels (Personal Edition)
    const parkingPhotoTypes = ['front', 'rear'];
    const parkingPhotoLabels = {
        front: language === 'el' ? 'Εμπρός Αυτοκίνητο' : 'Front Car',
        rear: language === 'el' ? 'Πίσω Αυτοκίνητο' : 'Rear Car'
    };

    // Fetch company edition
    const fetchCompanyEdition = useCallback(async () => {
        try {
            const response = await api.get('/auth/company');
            setCompanyEdition(response.data.edition || 'carrier');
        } catch (error) {
            console.error('Error fetching company edition:', error);
        }
    }, []);

    const fetchDriverStats = useCallback(async () => {
        try {
            const response = await api.get('/drivers/me/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch driver stats:', error);
            toast.error('Failed to load your stats');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchVehicles = useCallback(async () => {
        try {
            const response = await vehicleAPI.getAll();
            setVehicles(response.data);
        } catch (error) {
            console.error('Failed to fetch vehicles:', error);
        }
    }, []);

    useEffect(() => {
        fetchDriverStats();
        fetchVehicles();
        fetchCompanyEdition();
        fetchShiftSupervisor();
    }, [fetchDriverStats, fetchVehicles, fetchCompanyEdition]);

    // Fetch shift supervisor contact
    const fetchShiftSupervisor = useCallback(async () => {
        setLoadingSupervisor(true);
        try {
            const response = await api.get('/shift-supervisor');
            if (response.data && response.data.phone) {
                setShiftSupervisor(response.data);
            } else {
                setShiftSupervisor(null);
            }
        } catch (error) {
            console.error('Failed to fetch shift supervisor:', error);
            setShiftSupervisor(null);
        } finally {
            setLoadingSupervisor(false);
        }
    }, []);

    // Check for active check-in to enable/disable check-out button
    const checkActiveCheckIn = useCallback(async () => {
        try {
            // Only check TODAY's inspection - not past inspections
            const response = await api.get('/inspections/today/mine');
            // Response.data could be null if no inspection exists, or an object
            const inspection = response.data;
            if (inspection && inspection.id && !inspection.checkout_completed) {
                setHasActiveCheckIn(true);
                setLastInspection(inspection);
                console.log('Active check-in found:', inspection.id);
            } else {
                setHasActiveCheckIn(false);
                setLastInspection(null);
                console.log('No active check-in for today');
            }
        } catch (error) {
            // 404 means no inspection today - that's expected
            console.log('checkActiveCheckIn error:', error?.response?.status);
            setHasActiveCheckIn(false);
            setLastInspection(null);
        }
    }, []);

    useEffect(() => {
        checkActiveCheckIn();
    }, [checkActiveCheckIn]);

    // Refresh active check-in status when page becomes visible (e.g., returning from check-in)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkActiveCheckIn();
                fetchDriverStats();
            }
        };
        
        const handleFocus = () => {
            checkActiveCheckIn();
            fetchDriverStats();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [checkActiveCheckIn, fetchDriverStats]);

    // Fetch damage reports when we have the last vehicle
    const fetchDamageReports = useCallback(async (vehicleId) => {
        setLoadingDamageReports(true);
        try {
            const response = await api.get(`/damage-reports?vehicle_id=${vehicleId}&limit=3`);
            setDamageReports(response.data || []);
        } catch (error) {
            console.error('Failed to fetch damage reports:', error);
        } finally {
            setLoadingDamageReports(false);
        }
    }, []);

    useEffect(() => {
        if (stats?.last_vehicle?.id) {
            fetchDamageReports(stats.last_vehicle.id);
        }
    }, [stats?.last_vehicle?.id, fetchDamageReports]);

    const handleCallAssistance = () => {
        setShowHelpModal(true);
    };

    const handleCallSupervisor = () => {
        if (stats?.last_vehicle?.id) {
            const vehicle = vehicles.find(v => v.id === stats.last_vehicle.id);
            if (vehicle?.assistance_phone) {
                window.location.href = `tel:${vehicle.assistance_phone}`;
            } else {
                toast.error(language === 'el' 
                    ? 'Δεν έχει οριστεί τηλέφωνο προϊσταμένου για αυτό το όχημα' 
                    : 'No supervisor phone number configured for this vehicle');
            }
        } else {
            toast.error(language === 'el' 
                ? 'Δεν βρέθηκε όχημα. Κάντε πρώτα check-in.' 
                : 'No recent vehicle found. Please do a check-in first.');
        }
        setShowHelpModal(false);
    };

    const handleShowAssistance = () => {
        setShowHelpModal(false);
        setShowAssistanceModal(true);
        getCurrentLocation();
    };

    const getCurrentLocation = () => {
        setLoadingLocation(true);
        setLocationError(null);
        
        if (!navigator.geolocation) {
            setLocationError(language === 'el' 
                ? 'Το GPS δεν υποστηρίζεται από τη συσκευή σας' 
                : 'Geolocation is not supported by your device');
            setLoadingLocation(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCurrentLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });
                setLoadingLocation(false);
            },
            (error) => {
                let errorMessage;
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = language === 'el' 
                            ? 'Δεν επιτρέψατε την πρόσβαση στην τοποθεσία' 
                            : 'Location permission denied';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = language === 'el' 
                            ? 'Η τοποθεσία δεν είναι διαθέσιμη' 
                            : 'Location unavailable';
                        break;
                    case error.TIMEOUT:
                        errorMessage = language === 'el' 
                            ? 'Λήξη χρόνου αναζήτησης τοποθεσίας' 
                            : 'Location request timed out';
                        break;
                    default:
                        errorMessage = language === 'el' 
                            ? 'Σφάλμα εντοπισμού τοποθεσίας' 
                            : 'Error getting location';
                }
                setLocationError(errorMessage);
                setLoadingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    const copyLocationToClipboard = () => {
        if (currentLocation) {
            const locationText = `${currentLocation.latitude}, ${currentLocation.longitude}`;
            navigator.clipboard.writeText(locationText);
            toast.success(language === 'el' 
                ? 'Η τοποθεσία αντιγράφηκε!' 
                : 'Location copied!');
        }
    };

    const openInGoogleMaps = () => {
        if (currentLocation) {
            const url = `https://www.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}`;
            window.open(url, '_blank');
        }
    };

    const handleMediaSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        
        if (!isImage && !isVideo) {
            toast.error('Please select an image or video file');
            return;
        }

        // Check video size limit
        if (isVideo && file.size > 50 * 1024 * 1024) {
            toast.error('Video must be under 50MB');
            return;
        }

        // For videos, check duration (max 10 seconds)
        if (isVideo) {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                window.URL.revokeObjectURL(video.src);
                if (video.duration > 10) {
                    toast.error('Video must be 10 seconds or less');
                    setDamageMedia(null);
                    setDamageMediaPreview(null);
                    return;
                }
                // Video is valid, set it
                setDamageMedia(file);
                setDamageMediaPreview({ type: 'video', url: URL.createObjectURL(file) });
            };
            video.src = URL.createObjectURL(file);
            return;
        }

        setDamageMedia(file);

        // Create preview for images
        if (isImage) {
            const reader = new FileReader();
            reader.onload = (e) => setDamageMediaPreview({ type: 'image', url: e.target.result });
            reader.readAsDataURL(file);
        }
    };

    const handleSubmitDamageReport = async () => {
        if (!damageVehicleId) {
            toast.error('Please select a vehicle');
            return;
        }
        if (!damageDescription.trim()) {
            toast.error('Please describe the damage');
            return;
        }

        setSubmittingDamage(true);
        try {
            const formData = new FormData();
            formData.append('vehicle_id', damageVehicleId);
            formData.append('description', damageDescription);
            if (damageMedia) {
                formData.append('media', damageMedia);
            }

            await api.post('/damage-reports', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Damage report submitted successfully');
            setShowDamageModal(false);
            resetDamageForm();
            
            // Refresh damage reports if we submitted for the last vehicle
            if (stats?.last_vehicle?.id && damageVehicleId === stats.last_vehicle.id) {
                fetchDamageReports(stats.last_vehicle.id);
            }
        } catch (error) {
            console.error('Failed to submit damage report:', error);
            toast.error('Failed to submit damage report');
        } finally {
            setSubmittingDamage(false);
        }
    };

    const resetDamageForm = () => {
        setDamageVehicleId('');
        setDamageDescription('');
        setDamageMedia(null);
        setDamageMediaPreview(null);
    };

    const openDamageModal = () => {
        // Pre-select last used vehicle if available
        if (stats?.last_vehicle?.id) {
            setDamageVehicleId(stats.last_vehicle.id);
        }
        setShowDamageModal(true);
    };

    const openFuelModal = () => {
        if (!stats?.last_vehicle?.id) {
            toast.error('No recent vehicle found. Please do a check-in first.');
            return;
        }
        setShowFuelModal(true);
    };

    const handleSubmitFuelRefill = async () => {
        if (!stats?.last_vehicle?.id) {
            toast.error('No vehicle selected');
            return;
        }
        if (!fuelLiters || parseFloat(fuelLiters) <= 0) {
            toast.error('Please enter valid fuel amount');
            return;
        }
        if (!fuelCost || parseFloat(fuelCost) <= 0) {
            toast.error('Please enter valid total cost');
            return;
        }

        setSubmittingFuel(true);
        try {
            await api.post('/fuel-logs/driver-refill', {
                vehicle_id: stats.last_vehicle.id,
                quantity_liters: parseFloat(fuelLiters),
                total_cost: parseFloat(fuelCost)
            });

            toast.success('Fuel refill logged successfully');
            setShowFuelModal(false);
            setFuelLiters('');
            setFuelCost('');
        } catch (error) {
            console.error('Failed to log fuel refill:', error);
            toast.error('Failed to log fuel refill');
        } finally {
            setSubmittingFuel(false);
        }
    };

    const openCheckOutModal = async () => {
        if (!stats?.last_vehicle?.id) {
            toast.error(language === 'el' ? 'Δεν βρέθηκε όχημα. Κάντε πρώτα Check-In.' : 'No vehicle found. Please do a Check-In first.');
            return;
        }
        
        // Fetch last inspection for this vehicle to get check-in details
        try {
            const response = await api.get('/inspections?limit=1');
            if (response.data.length > 0 && !response.data[0].checkout_completed) {
                const inspection = response.data[0];
                setLastInspection(inspection);
                // Restore photos already uploaded for this check-out
                const uploaded = {};
                (inspection.checkout_photos || []).forEach(p => {
                    uploaded[p.type] = `uploaded:${p.type}`;
                });
                setCheckOutPhotos(uploaded);
                setShowCheckOutModal(true);
            } else {
                toast.error(language === 'el' ? 'Δεν βρέθηκε ενεργό Check-In' : 'No active Check-In found');
            }
        } catch (error) {
            toast.error('Failed to fetch inspection data');
        }
    };

    const handleCheckOutPhotoUpload = async (photoType, file) => {
        if (!file || !lastInspection) return;
        
        setUploadingCheckOutPhoto(photoType);
        const formData = new FormData();
        formData.append('photo_type', photoType);
        formData.append('file', file);
        
        try {
            await api.post(`/inspections/${lastInspection.id}/checkout-photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setCheckOutPhotos(prev => ({
                ...prev,
                [photoType]: URL.createObjectURL(file)
            }));
            toast.success(`${checkOutPhotoLabels[photoType]} uploaded`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Photo upload failed');
        } finally {
            setUploadingCheckOutPhoto(null);
        }
    };

    const handleSubmitCheckOut = async () => {
        if (!lastInspection) return;
        
        if (!checkOutKm || parseFloat(checkOutKm) <= 0) {
            toast.error(language === 'el' ? 'Εισάγετε τα χιλιόμετρα' : 'Please enter current kilometers');
            return;
        }

        const missing = checkOutPhotoTypes.filter(t => !checkOutPhotos[t]);
        if (missing.length > 0) {
            toast.error(language === 'el'
                ? `Ανεβάστε και τις 6 φωτογραφίες. Λείπουν: ${missing.map(t => checkOutPhotoLabels[t]).join(', ')}`
                : `All 6 photos are required. Missing: ${missing.map(t => checkOutPhotoLabels[t]).join(', ')}`);
            return;
        }
        
        const checkInKm = lastInspection.odometer_reading || 0;
        const currentKm = parseFloat(checkOutKm);
        
        if (currentKm < checkInKm) {
            toast.error(language === 'el' ? 'Τα χιλιόμετρα δεν μπορούν να είναι λιγότερα από το Check-In' : 'Kilometers cannot be less than Check-In');
            return;
        }
        
        setSubmittingCheckOut(true);
        
        // Try to get current location for checkout
        let checkoutLocation = null;
        if (navigator.geolocation) {
            try {
                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: 0
                    });
                });
                checkoutLocation = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                };
            } catch (e) {
                console.log('Could not get checkout location:', e.message);
            }
        }
        
        try {
            const checkoutData = {
                odometer_reading: currentKm,
                distance_traveled: currentKm - checkInKm
            };
            
            if (checkoutLocation) {
                checkoutData.checkout_location = checkoutLocation;
            }
            
            await api.post(`/inspections/${lastInspection.id}/checkout`, checkoutData);
            
            // Vehicle mileage is now updated by the backend checkout endpoint
            
            toast.success(language === 'el'
                ? 'Check-Out ολοκληρώθηκε! Ο έλεγχος φωτογραφιών με AI εκτελείται.'
                : 'Check-Out completed! AI photo comparison is running.');
            setShowCheckOutModal(false);
            setCheckOutKm('');
            setCheckOutPhotos({});
            setLastInspection(null);
            setHasActiveCheckIn(false);  // Disable check-out button again
            fetchDriverStats();
        } catch (error) {
            console.error('Check-out failed:', error);
            toast.error(error.response?.data?.detail || 'Check-Out failed');
        } finally {
            setSubmittingCheckOut(false);
        }
    };

    // Cargo Photo Upload Handler (Two photos: rear and side)
    const handleCargoPhotoSelect = (photoType, e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error(language === 'el' ? 'Το αρχείο είναι πολύ μεγάλο (max 10MB)' : 'File too large (max 10MB)');
            return;
        }

        setCargoPhotos(prev => ({ ...prev, [photoType]: file }));
        setCargoPreviews(prev => ({ ...prev, [photoType]: URL.createObjectURL(file) }));
    };

    const handleCargoUpload = async () => {
        if (!cargoPhotos.rear || !cargoPhotos.side || !lastInspection) {
            toast.error(language === 'el' ? 'Απαιτούνται και οι δύο φωτογραφίες' : 'Both photos are required');
            return;
        }

        setUploadingCargo(true);
        try {
            const formData = new FormData();
            formData.append('rear_photo', cargoPhotos.rear);
            formData.append('side_photo', cargoPhotos.side);

            const response = await api.post(
                `/inspections/${lastInspection.id}/cargo-photos`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            setCargoAnalysis(response.data.analysis);
            toast.success(
                language === 'el' 
                    ? `Χωρητικότητα φορτίου: ${response.data.capacity_percentage}%` 
                    : `Cargo capacity: ${response.data.capacity_percentage}%`
            );
        } catch (error) {
            console.error('Cargo upload failed:', error);
            toast.error(language === 'el' ? 'Αποτυχία ανάλυσης φορτίου' : 'Cargo analysis failed');
        } finally {
            setUploadingCargo(false);
        }
    };

    const resetCargoModal = () => {
        setCargoPhotos({ rear: null, side: null });
        setCargoPreviews({ rear: null, side: null });
        setCargoAnalysis(null);
        setShowCargoModal(false);
    };

    // ==================== PARKING SAFETY FUNCTIONS (Personal Edition) ====================
    
    const handleParkingPhotoSelect = (position, e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error(language === 'el' ? 'Επιλέξτε αρχείο εικόνας' : 'Please select an image file');
            return;
        }

        setParkingPhotos(prev => ({ ...prev, [position]: file }));

        const reader = new FileReader();
        reader.onload = (e) => {
            setParkingPreviews(prev => ({ ...prev, [position]: e.target.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleSubmitParkingSafety = async () => {
        // At least one photo is required
        if (!parkingPhotos.front && !parkingPhotos.rear) {
            toast.error(language === 'el' 
                ? 'Προσθέστε τουλάχιστον μία φωτογραφία' 
                : 'Please add at least one photo');
            return;
        }

        setUploadingParkingSafety(true);
        try {
            const formData = new FormData();
            if (parkingPhotos.front) formData.append('front_photo', parkingPhotos.front);
            if (parkingPhotos.rear) formData.append('rear_photo', parkingPhotos.rear);
            
            // Add vehicle ID if available
            if (stats?.last_vehicle?.id) {
                formData.append('vehicle_id', stats.last_vehicle.id);
            }

            await api.post('/parking-safety', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success(language === 'el' 
                ? 'Η εγγραφή Parking Safety αποθηκεύτηκε!' 
                : 'Parking Safety record saved!');
            
            resetParkingSafetyModal();
            fetchParkingSafetyRecords();
        } catch (error) {
            console.error('Parking safety upload failed:', error);
            const errorMsg = error.response?.data?.detail || 
                (language === 'el' ? 'Αποτυχία αποθήκευσης' : 'Save failed');
            toast.error(errorMsg);
        } finally {
            setUploadingParkingSafety(false);
        }
    };

    const resetParkingSafetyModal = () => {
        setParkingPhotos({ front: null, rear: null });
        setParkingPreviews({ front: null, rear: null });
        setShowParkingSafetyModal(false);
    };

    const fetchParkingSafetyRecords = useCallback(async () => {
        if (!isPersonalEdition) return;
        try {
            const response = await api.get('/parking-safety?limit=5');
            setParkingSafetyRecords(response.data || []);
        } catch (error) {
            console.error('Error fetching parking safety records:', error);
        }
    }, [isPersonalEdition]);

    // Fetch parking safety records on mount (Personal Edition only)
    useEffect(() => {
        if (isPersonalEdition) {
            fetchParkingSafetyRecords();
        }
    }, [isPersonalEdition, fetchParkingSafetyRecords]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-4 pb-20" data-testid="driver-dashboard">
            {/* Welcome Header */}
            <div className="text-center pt-2 pb-4">
                <p className="text-slate-500 text-sm">Welcome back</p>
                <h1 className="text-2xl font-bold text-slate-800">{user?.name}</h1>
            </div>

            {/* Quick Action - Check In Button */}
            <Button
                onClick={() => navigate('/app/check-in')}
                className="w-full bg-gradient-to-r from-[#1B7EB5] via-[#58A6B4] to-[#8CC774] text-white h-16 text-lg font-semibold rounded-xl shadow-lg active:scale-[0.98] transition-transform"
                data-testid="quick-checkin-btn"
            >
                <Camera className="w-6 h-6 mr-3" />
                {language === 'el' ? 'Έναρξη Check-In' : 'Start Daily Check-In'}
                <ChevronRight className="w-5 h-5 ml-2" />
            </Button>

            {/* Action Buttons - 5 columns */}
            <div className="grid grid-cols-5 gap-2">
                {/* Check-Out Button - Locked until Check-In is completed */}
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={hasActiveCheckIn ? openCheckOutModal : undefined}
                                disabled={!hasActiveCheckIn}
                                className={`h-14 rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1 ${
                                    hasActiveCheckIn 
                                        ? 'bg-purple-600 hover:bg-purple-500 text-white' 
                                        : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                }`}
                                data-testid="checkout-btn"
                            >
                                <LogOut className="w-5 h-5" />
                                <span className="text-[10px]">{language === 'el' ? 'Check-Out' : 'Check-Out'}</span>
                            </Button>
                        </TooltipTrigger>
                        {!hasActiveCheckIn && (
                            <TooltipContent side="bottom" className="max-w-[200px] bg-slate-900 text-white p-2 text-xs">
                                {language === 'el' 
                                    ? 'Κάντε πρώτα Check-In για να ενεργοποιηθεί το Check-Out'
                                    : 'Complete a Check-In first to enable Check-Out'}
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
                {/* Cargo Photo Button - Carrier Edition Only */}
                {CARGO_FEATURES_ENABLED && isCarrierEdition && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={async () => {
                                    // Check for active inspection first
                                    try {
                                        const response = await api.get('/inspections?limit=1');
                                        if (response.data.length > 0 && !response.data[0].checkout_completed) {
                                            setLastInspection(response.data[0]);
                                            setShowCargoModal(true);
                                        } else {
                                            toast.info(language === 'el' ? 'Κάντε Check-In πρώτα' : 'Check-In first');
                                        }
                                    } catch (error) {
                                        toast.info(language === 'el' ? 'Κάντε Check-In πρώτα' : 'Check-In first');
                                    }
                                }}
                                className="h-14 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1 relative"
                                data-testid="cargo-photo-btn"
                            >
                                <Package className="w-5 h-5" />
                                <span className="text-[10px]">{language === 'el' ? 'Φορτίο' : 'Cargo'}</span>
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                                    <Info className="w-3 h-3 text-indigo-600" />
                                </div>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[200px] bg-slate-900 text-white p-2 text-xs">
                            {language === 'el' 
                                ? 'Φωτογραφίστε το φορτίο σας. Το AI θα υπολογίσει το ποσοστό χωρητικότητας που χρησιμοποιείτε.'
                                : 'Photo your cargo area. AI will calculate the capacity percentage you\'re using.'}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                )}
                {/* Parking Safety Button - Personal Edition Only */}
                {isPersonalEdition && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={() => setShowParkingSafetyModal(true)}
                                className="h-14 bg-violet-600 hover:bg-violet-500 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1 relative"
                                data-testid="parking-safety-btn"
                            >
                                <Camera className="w-5 h-5" />
                                <span className="text-[10px]">{language === 'el' ? 'Safety' : 'Safety'}</span>
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                                    <Car className="w-3 h-3 text-violet-600" />
                                </div>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[200px] bg-slate-900 text-white p-2 text-xs">
                            {language === 'el' 
                                ? 'Φωτογραφίστε τα αυτοκίνητα μπροστά και πίσω σας για ασφάλεια στάθμευσης.'
                                : 'Photo the cars in front and behind you for parking safety.'}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                )}
                {/* Park Here Button - Personal Edition Only - Opens Parking Map */}
                {isPersonalEdition && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={() => navigate('/app/parking-map')}
                                className="h-14 bg-purple-600 hover:bg-purple-500 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1 relative"
                                data-testid="park-here-btn"
                            >
                                <ParkingCircle className="w-5 h-5" />
                                <span className="text-[10px]">{language === 'el' ? 'Park Here' : 'Park Here'}</span>
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                                    <MapPin className="w-3 h-3 text-purple-600" />
                                </div>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[200px] bg-slate-900 text-white p-2 text-xs">
                            {language === 'el' 
                                ? 'Σημειώστε στον χάρτη πού παρκάρατε το αυτοκίνητό σας.'
                                : 'Mark on the map where you parked your car.'}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                )}
                
                {/* Call Shift Supervisor Button */}
                {shiftSupervisor?.phone && (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    onClick={() => window.location.href = `tel:${shiftSupervisor.phone}`}
                                    className="h-14 bg-purple-600 hover:bg-purple-500 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1"
                                    data-testid="call-supervisor-btn"
                                >
                                    <PhoneCall className="w-5 h-5" />
                                    <span className="text-[10px] leading-tight text-center">
                                        {language === 'el' ? 'Προϊστ.' : 'Superv.'}
                                    </span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="bg-slate-900 text-white p-2 text-xs">
                                <p className="font-medium">{shiftSupervisor.name || (language === 'el' ? 'Υπεύθυνος Βάρδιας' : 'Shift Supervisor')}</p>
                                <p className="text-slate-300">{shiftSupervisor.phone}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
                
                <Button
                    onClick={handleCallAssistance}
                    className="h-14 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1"
                    data-testid="call-assistance-btn"
                >
                    <Phone className="w-5 h-5" />
                    <span className="text-[10px]">{language === 'el' ? 'Βοήθεια' : 'Help'}</span>
                </Button>
                <Button
                    onClick={openDamageModal}
                    className="h-14 bg-amber-600 hover:bg-amber-500 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1"
                    data-testid="report-damage-btn"
                >
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-[10px]">{language === 'el' ? 'Ζημιά' : 'Damage'}</span>
                </Button>
                <Button
                    onClick={openFuelModal}
                    className="h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform flex flex-col items-center justify-center gap-1 px-1"
                    data-testid="refill-fuel-btn"
                >
                    <Fuel className="w-5 h-5" />
                    <span className="text-[10px]">{language === 'el' ? 'Καύσιμα' : 'Fuel'}</span>
                </Button>
            </div>

            {/* Stats Grid - 2x2 */}
            <div className="grid grid-cols-2 gap-3">
                {/* Total KM */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center">
                            <Route className="w-4 h-4 text-[#1B7EB5]" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.total_km_driven?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-500">Total KM Driven</p>
                </div>

                {/* Total Check-ins */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-[#8CC774]/10 rounded-lg flex items-center justify-center">
                            <Camera className="w-4 h-4 text-[#8CC774]" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.total_inspections || 0}</p>
                    <p className="text-xs text-slate-500">Total Check-ins</p>
                </div>

                {/* This Month KM */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-[#58A6B4]/10 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-[#58A6B4]" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.km_this_month?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-500">KM This Month</p>
                </div>

                {/* Vehicles Used */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center">
                            <Truck className="w-4 h-4 text-[#4ECDC4]" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-800">{stats?.vehicles_used || 0}</p>
                    <p className="text-xs text-slate-500">Vehicles Driven</p>
                </div>
            </div>

            {/* Streak Card */}
            {stats?.current_streak > 0 && (
                <div className="bg-gradient-to-r from-[#800020]/20 to-[#722F37]/20 border border-[#800020]/30 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#800020]/20 rounded-full flex items-center justify-center">
                        <Flame className="w-6 h-6 text-[#800020]" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-[#800020]">{stats.current_streak} Day Streak!</p>
                        <p className="text-sm text-[#722F37]">Keep up the great work!</p>
                    </div>
                </div>
            )}

            {/* Last Vehicle Used */}
            {stats?.last_vehicle && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-slate-500">Last Vehicle Used</h3>
                        <span className="text-xs text-slate-400">{stats.last_vehicle.last_date}</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-[#1B7EB5]/10 rounded-xl flex items-center justify-center">
                            <Truck className="w-7 h-7 text-[#1B7EB5]" />
                        </div>
                        <div className="flex-1">
                            <p className="text-slate-800 font-semibold">{stats.last_vehicle.info?.split('(')[0]}</p>
                            <p className="text-sm text-[#1B7EB5]">{stats.last_vehicle.license_plate}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-slate-800 font-medium">{stats.last_vehicle.last_odometer?.toLocaleString()}</p>
                            <p className="text-xs text-slate-500">km</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Recent Activity */}
            {stats?.recent_activity?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="p-4 border-b border-slate-200">
                        <h3 className="text-sm font-medium text-slate-500">Recent Activity</h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {stats.recent_activity.map((activity, index) => (
                            <div key={index} className="p-4 flex items-center gap-4">
                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-slate-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-slate-800 font-medium truncate">{activity.vehicle_info}</p>
                                    <p className="text-xs text-slate-500">{activity.date} • {activity.photos_count} photos</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[#8CC774] font-medium">{activity.odometer?.toLocaleString()}</p>
                                    <p className="text-xs text-slate-500">km</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!stats?.recent_activity?.length && (
                <div className="text-center py-8 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Camera className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-600 mb-2">No check-ins yet</p>
                    <p className="text-sm text-slate-400">Start your first daily check-in above!</p>
                </div>
            )}

            {/* This Week Summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h3 className="text-sm font-medium text-slate-500 mb-3">This Week</h3>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#8CC774]/10 rounded-lg flex items-center justify-center">
                            <Clock className="w-5 h-5 text-[#8CC774]" />
                        </div>
                        <div>
                            <p className="text-slate-800 font-medium">{stats?.inspections_this_week || 0} Check-ins</p>
                            <p className="text-xs text-slate-500">Last 7 days</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center">
                            <Award className="w-5 h-5 text-[#1B7EB5]" />
                        </div>
                        <div>
                            <p className="text-slate-800 font-medium">{stats?.inspections_this_month || 0} This Month</p>
                            <p className="text-xs text-slate-500">Check-ins</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Damage Reports for Last Vehicle */}
            {stats?.last_vehicle && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            <h3 className="text-sm font-medium text-slate-700">Damage Reports</h3>
                        </div>
                        <span className="text-xs text-slate-400">{stats.last_vehicle.license_plate}</span>
                    </div>
                    
                    {loadingDamageReports ? (
                        <div className="p-8 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-[#1B7EB5] animate-spin" />
                        </div>
                    ) : damageReports.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                            {damageReports.slice(0, 5).map((report) => (
                                <div 
                                    key={report.id} 
                                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                                    onClick={() => setSelectedDamageReport(report)}
                                    data-testid={`damage-report-${report.id}`}
                                >
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                        report.status === 'pending' ? 'bg-amber-100' :
                                        report.status === 'reviewed' ? 'bg-blue-100' : 'bg-green-100'
                                    }`}>
                                        <AlertTriangle className={`w-5 h-5 ${
                                            report.status === 'pending' ? 'text-amber-600' :
                                            report.status === 'reviewed' ? 'text-blue-600' : 'text-green-600'
                                        }`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-slate-800 font-medium text-sm truncate">{report.description}</p>
                                        <p className="text-xs text-slate-500">{new Date(report.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                                            report.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                            report.status === 'reviewed' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                        }`}>
                                            {report.status}
                                        </span>
                                        <Eye className="w-4 h-4 text-slate-400" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-6 text-center">
                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <AlertTriangle className="w-6 h-6 text-slate-400" />
                            </div>
                            <p className="text-slate-500 text-sm">No damage reports for this vehicle</p>
                            <p className="text-xs text-slate-400 mt-1">Report any damages using the button above</p>
                        </div>
                    )}
                </div>
            )}

            {/* Damage Report View Modal */}
            <Dialog open={!!selectedDamageReport} onOpenChange={() => setSelectedDamageReport(null)}>
                <DialogContent className="bg-white border-slate-200 max-w-lg mx-4">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Damage Report Details
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedDamageReport && (
                        <div className="space-y-4 mt-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-500">Status</span>
                                <span className={`text-sm px-3 py-1 rounded-full capitalize ${
                                    selectedDamageReport.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                    selectedDamageReport.status === 'reviewed' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                }`}>
                                    {selectedDamageReport.status}
                                </span>
                            </div>
                            
                            <div>
                                <span className="text-sm text-slate-500">Date Reported</span>
                                <p className="text-slate-800 mt-1">{new Date(selectedDamageReport.created_at).toLocaleString()}</p>
                            </div>
                            
                            <div>
                                <span className="text-sm text-slate-500">Description</span>
                                <p className="text-slate-800 mt-1">{selectedDamageReport.description}</p>
                            </div>
                            
                            {selectedDamageReport.media_url && (
                                <div>
                                    <span className="text-sm text-slate-500">Media</span>
                                    <div className="mt-2 rounded-lg overflow-hidden">
                                        {selectedDamageReport.media_url.includes('.mp4') || selectedDamageReport.media_url.includes('video') ? (
                                            <video 
                                                src={`${process.env.REACT_APP_BACKEND_URL}/api/damage-reports/${selectedDamageReport.id}/media?token=${localStorage.getItem('fleet_token')}`}
                                                controls 
                                                className="w-full max-h-64 object-cover"
                                            />
                                        ) : (
                                            <img 
                                                src={`${process.env.REACT_APP_BACKEND_URL}/api/damage-reports/${selectedDamageReport.id}/media?token=${localStorage.getItem('fleet_token')}`}
                                                alt="Damage" 
                                                className="w-full max-h-64 object-cover"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            <Button
                                onClick={() => setSelectedDamageReport(null)}
                                className="w-full bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                            >
                                Close
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Damage Report Modal */}
            <Dialog open={showDamageModal} onOpenChange={setShowDamageModal}>
                <DialogContent className="bg-white border-slate-200 max-w-lg mx-4">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Report Damage/Scratch
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Vehicle Selection */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">Select Vehicle</Label>
                            <Select value={damageVehicleId} onValueChange={setDamageVehicleId}>
                                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="damage-vehicle-select">
                                    <SelectValue placeholder="Select a vehicle" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    {vehicles.map(vehicle => (
                                        <SelectItem key={vehicle.id} value={vehicle.id}>
                                            {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">Describe the Damage</Label>
                            <Textarea
                                value={damageDescription}
                                onChange={(e) => setDamageDescription(e.target.value)}
                                placeholder="Describe the damage or scratch location and severity..."
                                className="bg-white border-slate-300 text-slate-900 min-h-[100px] resize-none"
                                data-testid="damage-description-input"
                            />
                        </div>

                        {/* Media Upload */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">Upload Photo or Video (optional)</Label>
                            <p className="text-xs text-slate-500 mb-2">Max 10-15 second video or photo</p>
                            
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                capture="environment"
                                onChange={handleMediaSelect}
                                className="hidden"
                                data-testid="damage-media-input"
                            />

                            {!damageMediaPreview ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="h-20 border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-[#1B7EB5] flex flex-col gap-2"
                                    >
                                        <Image className="w-6 h-6" />
                                        <span className="text-xs">Take Photo</span>
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="h-20 border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-[#1B7EB5] flex flex-col gap-2"
                                    >
                                        <Video className="w-6 h-6" />
                                        <span className="text-xs">Record Video</span>
                                    </Button>
                                </div>
                            ) : (
                                <div className="relative">
                                    {damageMediaPreview.type === 'image' ? (
                                        <img 
                                            src={damageMediaPreview.url} 
                                            alt="Damage preview" 
                                            className="w-full h-48 object-cover rounded-lg"
                                        />
                                    ) : (
                                        <video 
                                            src={damageMediaPreview.url} 
                                            className="w-full h-48 object-cover rounded-lg"
                                            controls
                                        />
                                    )}
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        size="sm"
                                        className="absolute top-2 right-2"
                                        onClick={() => {
                                            setDamageMedia(null);
                                            setDamageMediaPreview(null);
                                        }}
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowDamageModal(false);
                                    resetDamageForm();
                                }}
                                className="flex-1 border-slate-300 text-slate-600"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitDamageReport}
                                disabled={submittingDamage || !damageVehicleId || !damageDescription.trim()}
                                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
                                data-testid="submit-damage-btn"
                            >
                                {submittingDamage ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Check className="w-5 h-5 mr-2" />
                                        Submit Report
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Fuel Refill Modal */}
            <Dialog open={showFuelModal} onOpenChange={setShowFuelModal}>
                <DialogContent className="bg-white border-slate-200 max-w-md mx-4">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Fuel className="w-5 h-5 text-blue-500" />
                            Refill Fuel
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Vehicle Info */}
                        {stats?.last_vehicle && (
                            <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center">
                                    <Truck className="w-5 h-5 text-[#1B7EB5]" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-800">
                                        {stats.last_vehicle.info?.split('(')[0]}
                                    </p>
                                    <p className="text-xs text-slate-500">{stats.last_vehicle.license_plate}</p>
                                </div>
                            </div>
                        )}

                        {/* Liters Input */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">Fuel Amount (Liters)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={fuelLiters}
                                onChange={(e) => setFuelLiters(e.target.value)}
                                placeholder="e.g., 45.5"
                                className="bg-white border-slate-300 text-slate-900"
                                data-testid="fuel-liters-input"
                            />
                        </div>

                        {/* Total Cost Input */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">Total Cost (€)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={fuelCost}
                                onChange={(e) => setFuelCost(e.target.value)}
                                placeholder="e.g., 75.00"
                                className="bg-white border-slate-300 text-slate-900"
                                data-testid="fuel-cost-input"
                            />
                        </div>

                        {/* Price per Liter Display */}
                        {fuelLiters && fuelCost && parseFloat(fuelLiters) > 0 && (
                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                <p className="text-xs text-blue-600">Price per Liter</p>
                                <p className="text-lg font-bold text-blue-700">
                                    €{(parseFloat(fuelCost) / parseFloat(fuelLiters)).toFixed(3)}
                                </p>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowFuelModal(false);
                                    setFuelLiters('');
                                    setFuelCost('');
                                }}
                                className="flex-1 border-slate-300 text-slate-600"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitFuelRefill}
                                disabled={submittingFuel || !fuelLiters || !fuelCost}
                                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                                data-testid="submit-fuel-btn"
                            >
                                {submittingFuel ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Check className="w-5 h-5 mr-2" />
                                        Log Refill
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Check Out Modal */}
            <Dialog open={showCheckOutModal} onOpenChange={setShowCheckOutModal}>
                <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto mx-4">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <LogOut className="w-5 h-5 text-purple-500" />
                            {language === 'el' ? 'Check-Out Οχήματος' : 'Vehicle Check-Out'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Vehicle Info */}
                        {lastInspection && (
                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                                        <Truck className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-800">
                                            {stats?.last_vehicle?.info || 'Vehicle'}
                                        </p>
                                        <p className="text-sm text-purple-600">
                                            {language === 'el' ? 'Check-In χιλιόμετρα' : 'Check-In kilometers'}: {lastInspection.odometer_reading?.toLocaleString() || 0} km
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Current Kilometers */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{language === 'el' ? 'Τρέχοντα Χιλιόμετρα' : 'Current Kilometers'} *</Label>
                            <Input
                                type="number"
                                value={checkOutKm}
                                onChange={(e) => setCheckOutKm(e.target.value)}
                                placeholder={language === 'el' ? 'π.χ., 45500' : 'e.g., 45500'}
                                className="bg-white border-slate-300 text-slate-900"
                                data-testid="checkout-km-input"
                            />
                            {checkOutKm && lastInspection?.odometer_reading && parseFloat(checkOutKm) >= lastInspection.odometer_reading && (
                                <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                                    <p className="text-xs text-green-600">{language === 'el' ? 'Απόσταση που διανύθηκε' : 'Distance Traveled'}</p>
                                    <p className="text-xl font-bold text-green-700">
                                        {(parseFloat(checkOutKm) - lastInspection.odometer_reading).toLocaleString()} km
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Photo Upload Section */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{language === 'el' ? 'Φωτογραφίες Check-Out' : 'Check-Out Photos'}</Label>
                            <p className="text-xs text-slate-500">
                                {language === 'el' ? 'Τραβήξτε φωτογραφίες του οχήματος κατά την επιστροφή' : 'Take photos of the vehicle upon return'}
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                                {checkOutPhotoTypes.map(photoType => (
                                    <div key={photoType} className="relative">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            className="hidden"
                                            ref={el => checkOutFileRefs.current[photoType] = el}
                                            onChange={(e) => handleCheckOutPhotoUpload(photoType, e.target.files[0])}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => checkOutFileRefs.current[photoType]?.click()}
                                            disabled={uploadingCheckOutPhoto === photoType}
                                            className={`w-full aspect-square rounded-lg border-2 border-dashed transition-all flex flex-col items-center justify-center ${
                                                checkOutPhotos[photoType]
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-slate-300 hover:border-purple-400 hover:bg-purple-50'
                                            }`}
                                        >
                                            {uploadingCheckOutPhoto === photoType ? (
                                                <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                                            ) : checkOutPhotos[photoType] ? (
                                                <>
                                                    <Check className="w-6 h-6 text-green-500" />
                                                    <span className="text-xs text-green-600 mt-1">✓</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Camera className="w-6 h-6 text-slate-400" />
                                                    <span className="text-xs text-slate-500 mt-1 text-center px-1">{checkOutPhotoLabels[photoType]}</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-slate-500 text-center">
                                {Object.keys(checkOutPhotos).length}/6 {language === 'el' ? 'φωτογραφίες' : 'photos uploaded'}
                            </p>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowCheckOutModal(false);
                                    setCheckOutKm('');
                                    setCheckOutPhotos({});
                                    setLastInspection(null);
                                }}
                                className="flex-1 border-slate-300 text-slate-600"
                            >
                                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                            </Button>
                            <Button
                                onClick={handleSubmitCheckOut}
                                disabled={submittingCheckOut || !checkOutKm}
                                className="flex-1 bg-purple-600 hover:bg-purple-500 text-white"
                                data-testid="submit-checkout-btn"
                            >
                                {submittingCheckOut ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Check className="w-5 h-5 mr-2" />
                                        {language === 'el' ? 'Ολοκλήρωση' : 'Complete Check-Out'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Cargo Photo Modal - Two Photos Required */}
            <Dialog open={showCargoModal} onOpenChange={setShowCargoModal}>
                <DialogContent className="bg-white border-slate-200 max-w-md mx-auto max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Package className="w-5 h-5 text-indigo-600" />
                            {language === 'el' ? 'Ανάλυση Φορτίου' : 'Cargo Analysis'}
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button className="ml-1 text-slate-400 hover:text-indigo-600">
                                            <Info className="w-4 h-4" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-[250px] bg-slate-900 text-white p-3 text-sm">
                                        {language === 'el' 
                                            ? 'Τραβήξτε δύο φωτογραφίες του χώρου φορτίου: μία από την πίσω πόρτα και μία από την πλαϊνή. Το AI θα αναλύσει και θα υπολογίσει το ποσοστό χωρητικότητας.'
                                            : 'Take two photos of your cargo area: one from the rear door and one from the side door. AI will analyze and calculate capacity percentage.'}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Photo counter */}
                        <div className="text-center text-sm text-slate-500">
                            {Object.values(cargoPhotos).filter(Boolean).length}/2 {language === 'el' ? 'φωτογραφίες' : 'photos'}
                        </div>

                        {/* Two Photo Upload Slots */}
                        <div className="grid grid-cols-2 gap-3">
                            {cargoPhotoTypes.map((photoType) => (
                                <div key={photoType} className="space-y-1">
                                    <input
                                        ref={el => cargoFileRefs.current[photoType] = el}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={(e) => handleCargoPhotoSelect(photoType, e)}
                                        className="hidden"
                                    />
                                    <p className="text-xs font-medium text-slate-600 text-center">
                                        {cargoPhotoLabels[photoType]}
                                    </p>
                                    {!cargoPreviews[photoType] ? (
                                        <button
                                            onClick={() => cargoFileRefs.current[photoType]?.click()}
                                            className="w-full h-32 border-2 border-dashed border-indigo-300 rounded-xl flex flex-col items-center justify-center gap-1 text-indigo-600 hover:bg-indigo-50 transition-colors"
                                        >
                                            <Camera className="w-8 h-8" />
                                            <span className="text-xs">
                                                {language === 'el' ? 'Φωτογραφία' : 'Take Photo'}
                                            </span>
                                        </button>
                                    ) : (
                                        <div className="relative">
                                            <img 
                                                src={cargoPreviews[photoType]} 
                                                alt={cargoPhotoLabels[photoType]} 
                                                className="w-full h-32 object-cover rounded-xl"
                                            />
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                className="absolute top-1 right-1 h-6 w-6 p-0"
                                                onClick={() => {
                                                    setCargoPhotos(prev => ({ ...prev, [photoType]: null }));
                                                    setCargoPreviews(prev => ({ ...prev, [photoType]: null }));
                                                }}
                                            >
                                                <X className="w-3 h-3" />
                                            </Button>
                                            <div className="absolute bottom-1 left-1 bg-green-500 text-white rounded-full p-1">
                                                <Check className="w-3 h-3" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Analysis Results */}
                        {cargoAnalysis && (
                            <div className="bg-indigo-50 rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-slate-700">
                                        {language === 'el' ? 'Χωρητικότητα' : 'Capacity'}
                                    </span>
                                    <span className="text-2xl font-bold text-indigo-600">
                                        {cargoAnalysis.capacity_percentage}%
                                    </span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-3">
                                    <div 
                                        className="bg-indigo-600 h-3 rounded-full transition-all"
                                        style={{ width: `${cargoAnalysis.capacity_percentage}%` }}
                                    />
                                </div>
                                <div className="text-sm text-slate-600">
                                    <p><strong>{language === 'el' ? 'Τύπος φορτίου:' : 'Load type:'}</strong> {cargoAnalysis.load_type}</p>
                                    <p><strong>{language === 'el' ? 'Αποδοτικότητα:' : 'Efficiency:'}</strong> {cargoAnalysis.space_efficiency}</p>
                                    <p className="mt-2 text-xs">{cargoAnalysis.description}</p>
                                </div>
                                {cargoAnalysis.recommendations?.length > 0 && (
                                    <div className="text-xs text-indigo-700 bg-indigo-100 rounded p-2">
                                        <strong>{language === 'el' ? 'Συστάσεις:' : 'Tips:'}</strong>
                                        <ul className="list-disc list-inside mt-1">
                                            {cargoAnalysis.recommendations.map((rec, i) => (
                                                <li key={i}>{rec}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <p className="text-xs text-green-600 text-center mt-2">
                                    ✓ {language === 'el' ? 'Η αναφορά εστάλη στον διαχειριστή' : 'Report sent to fleet manager'}
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={resetCargoModal}
                                className="flex-1 border-slate-300"
                            >
                                {cargoAnalysis 
                                    ? (language === 'el' ? 'Κλείσιμο' : 'Close')
                                    : (language === 'el' ? 'Ακύρωση' : 'Cancel')
                                }
                            </Button>
                            {!cargoAnalysis && (
                                <Button
                                    onClick={handleCargoUpload}
                                    disabled={!cargoPhotos.rear || !cargoPhotos.side || uploadingCargo}
                                    className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white"
                                >
                                    {uploadingCargo ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Upload className="w-5 h-5 mr-2" />
                                            {language === 'el' ? 'Ανάλυση' : 'Analyze'}
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Help Options Modal */}
            <Dialog open={showHelpModal} onOpenChange={setShowHelpModal}>
                <DialogContent className="max-w-[340px] bg-white rounded-2xl p-0 overflow-hidden">
                    <DialogHeader className="bg-emerald-600 p-4">
                        <DialogTitle className="text-white text-lg flex items-center gap-2">
                            <Phone className="w-5 h-5" />
                            {language === 'el' ? 'Βοήθεια' : 'Help'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="p-4 space-y-3">
                        <p className="text-sm text-slate-600 text-center mb-4">
                            {language === 'el' 
                                ? 'Επιλέξτε τον τύπο βοήθειας που χρειάζεστε' 
                                : 'Select the type of help you need'}
                        </p>
                        
                        {/* Assistance Option - GPS Location */}
                        <Button
                            onClick={handleShowAssistance}
                            className="w-full h-16 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl shadow-lg flex items-center justify-start gap-4 px-4"
                            data-testid="assistance-option-btn"
                        >
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                <MapPin className="w-6 h-6" />
                            </div>
                            <div className="text-left">
                                <p className="font-semibold">{language === 'el' ? 'Οδική Βοήθεια' : 'Roadside Assistance'}</p>
                                <p className="text-xs text-white/80">{language === 'el' ? 'Εμφάνιση τοποθεσίας GPS' : 'Show GPS location'}</p>
                            </div>
                        </Button>

                        <Button
                            variant="outline"
                            onClick={() => setShowHelpModal(false)}
                            className="w-full mt-2 border-slate-300"
                        >
                            {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Assistance Location Modal */}
            <Dialog open={showAssistanceModal} onOpenChange={setShowAssistanceModal}>
                <DialogContent className="max-w-[380px] bg-white rounded-2xl p-0 overflow-hidden">
                    <DialogHeader className="bg-gradient-to-r from-orange-500 to-red-500 p-4">
                        <DialogTitle className="text-white text-lg flex items-center gap-2">
                            <Navigation className="w-5 h-5" />
                            {language === 'el' ? 'Η Τοποθεσία σας' : 'Your Location'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="p-4 space-y-4">
                        {loadingLocation ? (
                            <div className="flex flex-col items-center py-8">
                                <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-3" />
                                <p className="text-slate-600">
                                    {language === 'el' ? 'Εντοπισμός τοποθεσίας...' : 'Getting location...'}
                                </p>
                            </div>
                        ) : locationError ? (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <AlertTriangle className="w-8 h-8 text-red-500" />
                                </div>
                                <p className="text-red-600 font-medium mb-2">{locationError}</p>
                                <Button
                                    onClick={getCurrentLocation}
                                    variant="outline"
                                    className="mt-2"
                                >
                                    {language === 'el' ? 'Δοκιμάστε ξανά' : 'Try again'}
                                </Button>
                            </div>
                        ) : currentLocation ? (
                            <>
                                {/* Map Preview */}
                                <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                                    <iframe
                                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${currentLocation.longitude - 0.005},${currentLocation.latitude - 0.005},${currentLocation.longitude + 0.005},${currentLocation.latitude + 0.005}&layer=mapnik&marker=${currentLocation.latitude},${currentLocation.longitude}`}
                                        width="100%"
                                        height="200"
                                        style={{ border: 0 }}
                                        title="Location Map"
                                    />
                                </div>

                                {/* Coordinates Display */}
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                                    <p className="text-xs text-slate-500 mb-1">
                                        {language === 'el' ? 'Συντεταγμένες GPS' : 'GPS Coordinates'}
                                    </p>
                                    <p className="font-mono text-lg font-semibold text-slate-800">
                                        {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {language === 'el' ? 'Ακρίβεια:' : 'Accuracy:'} ±{Math.round(currentLocation.accuracy)}m
                                    </p>
                                </div>

                                {/* Action Buttons */}
                                <div className="grid grid-cols-2 gap-3">
                                    <Button
                                        onClick={copyLocationToClipboard}
                                        className="h-12 bg-slate-800 hover:bg-slate-700 text-white rounded-xl flex items-center justify-center gap-2"
                                        data-testid="copy-location-btn"
                                    >
                                        <Copy className="w-4 h-4" />
                                        {language === 'el' ? 'Αντιγραφή' : 'Copy'}
                                    </Button>
                                    <Button
                                        onClick={openInGoogleMaps}
                                        className="h-12 bg-blue-600 hover:bg-blue-500 text-white rounded-xl flex items-center justify-center gap-2"
                                        data-testid="open-maps-btn"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        {language === 'el' ? 'Google Maps' : 'Google Maps'}
                                    </Button>
                                </div>

                                {/* Instructions */}
                                <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                                    <p className="text-sm text-orange-800">
                                        <strong>{language === 'el' ? 'Οδηγίες:' : 'Instructions:'}</strong><br/>
                                        {language === 'el' 
                                            ? 'Αντιγράψτε τις συντεταγμένες ή ανοίξτε τον χάρτη για να τις δώσετε στην οδική βοήθεια.'
                                            : 'Copy the coordinates or open the map to share with roadside assistance.'}
                                    </p>
                                </div>

                                {/* Refresh Location */}
                                <Button
                                    onClick={getCurrentLocation}
                                    variant="outline"
                                    className="w-full border-slate-300"
                                >
                                    <Navigation className="w-4 h-4 mr-2" />
                                    {language === 'el' ? 'Ανανέωση τοποθεσίας' : 'Refresh location'}
                                </Button>
                            </>
                        ) : null}

                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAssistanceModal(false);
                                setCurrentLocation(null);
                                setLocationError(null);
                            }}
                            className="w-full border-slate-300"
                        >
                            {language === 'el' ? 'Κλείσιμο' : 'Close'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Parking Safety Modal - Personal Edition Only */}
            <Dialog open={showParkingSafetyModal} onOpenChange={(open) => !uploadingParkingSafety && setShowParkingSafetyModal(open)}>
                <DialogContent className="bg-white border-slate-200 max-w-md mx-4">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <ParkingCircle className="w-5 h-5 text-violet-600" />
                            {language === 'el' ? 'Parking Safety' : 'Parking Safety'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Info text */}
                        <div className="bg-violet-50 rounded-xl p-3 border border-violet-200">
                            <p className="text-sm text-violet-800">
                                {language === 'el' 
                                    ? 'Φωτογραφίστε τα αυτοκίνητα που είναι μπροστά και πίσω από το δικό σας για ασφάλεια.'
                                    : 'Take photos of the cars parked in front and behind yours for safety.'}
                            </p>
                        </div>

                        {/* Photo Capture Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            {parkingPhotoTypes.map((type) => (
                                <div key={type} className="space-y-2">
                                    <Label className="text-slate-700 text-sm font-medium flex items-center gap-2">
                                        <Car className="w-4 h-4" />
                                        {parkingPhotoLabels[type]}
                                    </Label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        ref={(el) => parkingFileRefs.current[type] = el}
                                        onChange={(e) => handleParkingPhotoSelect(type, e)}
                                        className="hidden"
                                    />
                                    <div 
                                        onClick={() => parkingFileRefs.current[type]?.click()}
                                        className={`aspect-video rounded-xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center transition-colors ${
                                            parkingPreviews[type] 
                                                ? 'border-violet-300 bg-violet-50' 
                                                : 'border-slate-300 hover:border-violet-400 hover:bg-violet-50'
                                        }`}
                                    >
                                        {parkingPreviews[type] ? (
                                            <div className="relative w-full h-full">
                                                <img 
                                                    src={parkingPreviews[type]} 
                                                    alt={parkingPhotoLabels[type]}
                                                    className="w-full h-full object-cover rounded-lg"
                                                />
                                                <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                                                    <Check className="w-3 h-3 text-white" />
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <Camera className="w-6 h-6 text-slate-400 mb-1" />
                                                <span className="text-xs text-slate-500">
                                                    {language === 'el' ? 'Πατήστε για φωτό' : 'Tap to capture'}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={resetParkingSafetyModal}
                                disabled={uploadingParkingSafety}
                                className="flex-1 border-slate-300"
                            >
                                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                            </Button>
                            <Button
                                onClick={handleSubmitParkingSafety}
                                disabled={uploadingParkingSafety || (!parkingPhotos.front && !parkingPhotos.rear)}
                                className="flex-1 bg-violet-600 hover:bg-violet-500 text-white"
                                data-testid="submit-parking-safety-btn"
                            >
                                {uploadingParkingSafety ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        {language === 'el' ? 'Αποθήκευση...' : 'Saving...'}
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4 mr-2" />
                                        {language === 'el' ? 'Αποθήκευση' : 'Save'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Parking Safety Records Section - Personal Edition Only */}
            {isPersonalEdition && parkingSafetyRecords.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                        <ParkingCircle className="w-4 h-4 text-violet-600" />
                        <h3 className="text-sm font-medium text-slate-700">
                            {language === 'el' ? 'Πρόσφατες Εγγραφές Parking Safety' : 'Recent Parking Safety Records'}
                        </h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {parkingSafetyRecords.slice(0, 3).map((record) => (
                            <div key={record.id} className="p-4 flex items-center gap-4">
                                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                                    <Car className="w-5 h-5 text-violet-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-slate-800 font-medium text-sm">
                                        {record.photos?.length || 0} {language === 'el' ? 'φωτογραφίες' : 'photos'}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        {new Date(record.created_at).toLocaleString()}
                                    </p>
                                </div>
                                <div className="flex -space-x-2">
                                    {record.photos?.slice(0, 2).map((photo, idx) => (
                                        <img 
                                            key={idx}
                                            src={photo.photo_url} 
                                            alt=""
                                            className="w-8 h-8 rounded-lg border-2 border-white object-cover"
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
